import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield } from "lucide-react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { useLanguage } from "@/hooks/use-language";

// Privacy Policy Page (Full Content)
const PrivacyPolicyPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const hideHeader = searchParams.get("header") === "false";
  const hideFooter = searchParams.get("footer") === "false";

  return (
    <div className="bg-[#030014] min-h-screen overflow-x-hidden">
      {!hideHeader && <Header />}
      <main className={hideHeader ? "pt-8 pb-20" : "pt-24 pb-20"}>
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          {/* Back Button */}
          {!hideHeader && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to="/policy"
                className="inline-flex items-center gap-2 text-white/60 hover:text-[#FF2E63] transition-colors mb-8"
                data-testid="back-policy-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                {t("nav.backToPolicies")}
              </Link>
            </motion.div>
          )}

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF2E63]/20 to-pink-600/10 flex items-center justify-center">
                <Shield className="w-7 h-7 text-[#FF2E63]" />
              </div>
              <div>
                <p className="text-[#FF2E63] text-xs tracking-[0.3em] uppercase">
                  {t("policy.legal")}
                </p>
                <h1 className="font-display font-bold text-3xl sm:text-4xl text-white">
                  {t("policy.privacyPolicy")}
                </h1>
              </div>
            </div>
            <p className="text-white/50">{t("policy.lastUpdated")}</p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-10 text-white/80 leading-relaxed"
          >
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                1. Introduction
              </h2>
              <p className="mb-4">
                Welcome to <strong>PHUONG</strong> ("we," "our," or "us").
                PHUONG is an AI-powered digital ecosystem encompassing a
                personalized AI companion (PHUONG), a social platform
                (PHUNSOCIAL), task management (PHUNTODO), personal finance
                tracking (PHUNMONEY), a music platform (PHUNMUSIC), and a
                creator and trading hub (PHUNCENTER, including a model
                marketplace, wallet, and leaderboards). The ecosystem is
                developed and operated by <strong>Phan Phúc Đạt</strong>, an
                independent developer based in Vietnam.
              </p>
              <p className="mb-4">
                This Privacy Policy explains how we collect, use, share, and
                protect your personal data when you use our mobile application
                ("App") and website. By using PHUONG, you agree to the practices
                described in this policy.
              </p>
              <p className="mb-4">
                <strong>
                  This service is intended for users who meet the minimum age
                  required by the applicable app store (distribution platform)
                  and applicable law.
                </strong>{" "}
                By using the service, you confirm that you meet that age
                requirement. We do not knowingly collect data from anyone who
                does not meet it.
              </p>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/70 text-sm">
                  <strong>Special Categories of Data:</strong> We do not
                  knowingly collect or store special categories of personal data
                  as defined under GDPR — including health status, biometric
                  data, racial or ethnic origin, political opinions, religious
                  beliefs, or sexual orientation. We encourage you not to share
                  such sensitive information in your conversations with PHUONG.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                2. Information We Collect
              </h2>
              <p className="mb-4">
                To provide and protect the service, we may collect and store the
                following categories of data:
              </p>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                A. Account &amp; Identity Information
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>Email Address:</strong> Required for account
                  authentication, OTP verification, and critical system
                  notifications.
                </li>
                <li>
                  <strong>Timezone, Country (ISO code) &amp; Language/Locale:</strong>{" "}
                  Used to display the App in your language and to synchronize
                  scheduling, local holidays, and regional content.
                </li>
                <li>
                  <strong>Social OAuth Identity:</strong> If you log in via
                  Google, Facebook, or Apple, we receive only a unique
                  identifier and your email. We do not store your social media
                  passwords.
                </li>
                <li>
                  <strong>Profile Information (optional):</strong> Display name,
                  username, avatar, background image, and bio — provided at your
                  discretion.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                B. Technical, Device &amp; Log Data
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>IP Address:</strong> Processed for security auditing,
                  rate limiting, abuse and fraud prevention, regional lookup in
                  certain flows, and legal compliance.
                </li>
                <li>
                  <strong>Device &amp; Browser Information:</strong> Device and
                  browser type, operating system, device identifiers, error
                  context, and technical logs — used to diagnose crashes and
                  maintain stability.
                </li>
                <li>
                  <strong>Advertising Identifiers:</strong> Where you allow it,
                  used for advertising (see Section 4).
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                C. AI Companion Interactions &amp; Memory
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>Chat Messages:</strong> Your conversations with PHUONG
                  are stored to maintain continuity. Authorized administrators
                  may access this data strictly for technical debugging, safety
                  moderation, or legal compliance.
                </li>
                <li>
                  <strong>AI Memory:</strong> PHUONG builds a personalized
                  understanding of you over time based on your conversations and
                  in-app activities. By using PHUONG, you consent to this
                  personalization, and you may request a reset (see Section 8).
                </li>
                <li>
                  <strong>Voice Input:</strong> Voice input is processed entirely
                  on your device using on-device speech recognition. We do not
                  upload or store your raw voice recordings on our servers.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                D. Images, Video &amp; Media Files
              </h3>
              <p className="mb-4">
                Any images, videos, cover art, audio files and music tracks
                (including content on PHUNMUSIC), and other media you send,
                upload, or create within the platform are{" "}
                <strong>
                  stored and secured on a third-party content delivery network
                  (CDN)
                </strong>{" "}
                that meets appropriate security standards.
              </p>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                E. Mini-App Data
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>PHUNMONEY &amp; PHUNTODO (Private):</strong> Your
                  financial data (income, expenses, funds, categories, budgets)
                  and your tasks/schedules are entirely user-inputted and treated
                  as private. We do <strong>not</strong> use them to train any
                  AI, and we do <strong>not</strong> sell or share them with any
                  third party (banks, credit bureaus, advertisers, etc.). PHUONG
                  does not connect to any bank or financial institution.
                </li>
                <li>
                  <strong>PHUNSOCIAL:</strong> Posts (text and media), comments,
                  likes, votes, and shares. Public posts are visible to all
                  authenticated users; private posts are visible only to you.
                </li>
                <li>
                  <strong>PHUNMUSIC:</strong> Listener profile, follows,
                  playlists, listening sessions and playback events, purchase
                  history, and search/browse behavior. For creators: songs, cover
                  art, audio files, lyrics, translations, genres, and copyright
                  information.
                </li>
                <li>
                  <strong>PHUNCENTER (Marketplace &amp; Wallet):</strong> Models
                  and assets you create or submit, transaction history, virtual
                  item balances, public creator profile, and ranking if you
                  participate in leaderboards.
                </li>
              </ul>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 mt-2 mb-4">
                <p className="text-white/70 text-sm">
                  <strong>Your Activity &amp; PHUONG:</strong> Your own PHUONG AI
                  companion may access your data and activity across the platform
                  (including your mini-app data) to assist you in conversation.
                  PHUONG cannot access any other user's data, and{" "}
                  <strong>no third party</strong> is given access to this private
                  data.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                F. Transaction &amp; Payment Data
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>Transaction History:</strong> We store records of
                  virtual-item transactions, including type, amount, balance
                  before and after, payment method, timestamps, and order
                  references.
                </li>
                <li>
                  <strong>Payment Methods:</strong> We do <strong>not</strong>{" "}
                  store your payment methods — credit card numbers, bank account
                  details, or other sensitive payment credentials. All payment
                  processing is handled by our third-party payment providers.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                G. Other Data Generated Through Use
              </h3>
              <p>
                We may also retain other data generated while you use the
                service, including preferences and settings, feature usage
                history, abuse reports, customer-support records, and other logs
                necessary to operate, secure, and comply with the law.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                3. How We Use Your Information
              </h2>
              <p className="mb-4">
                <strong>Legal Basis for Processing:</strong> Where applicable
                data protection laws apply (including the EU GDPR), we process
                your data on the bases of: performance of a contract (account
                and core service data); consent (AI memory and personalization,
                which you may withdraw at any time); legitimate interest (IP
                logging, security, fraud prevention, technical error
                monitoring); and legal obligation (financial transaction
                records). PHUONG is operated from Vietnam and does not currently
                maintain a designated representative in the EU/EEA, yet strives
                to uphold these principles for all users.
              </p>
              <p className="mb-4">We use the collected data to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide, operate, and improve the PHUONG ecosystem.</li>
                <li>
                  Build and maintain your AI companion's personalized memory.
                </li>
                <li>
                  Process AI responses and voice audio via designated
                  third-party providers.
                </li>
                <li>
                  Process payments and manage your virtual-item balances and
                  creator earnings.
                </li>
                <li>
                  Display your content on PHUNSOCIAL, PHUNMUSIC, and the
                  marketplace.
                </li>
                <li>
                  Send critical communications such as OTP codes and payment
                  confirmations.
                </li>
                <li>
                  Detect, investigate, and prevent fraud, security breaches, and
                  abuse; and serve advertising where applicable.
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                4. Third-Party Providers, AI &amp; Advertising
              </h2>
              <p className="mb-4">
                We do not sell your personal data. We share data only with
                trusted third-party providers necessary to operate the platform,
                in the categories below. We may update this list provided their
                data protection standards meet or exceed our requirements.
              </p>

              <div className="space-y-6">
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h3 className="text-white font-semibold mb-2">
                    LLM Processing (AI Conversations)
                  </h3>
                  <p className="text-white/70 text-sm">
                    The conversation content needed to generate responses is
                    transmitted to designated LLM providers. We configure
                    account-level settings and per-request technical flags to
                    route only to providers that commit not to retain or train
                    on the data, to the extent within our control.
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h3 className="text-white font-semibold mb-2">
                    Text-to-Speech (Voice Generation)
                  </h3>
                  <p className="text-white/70 text-sm">
                    Only the text of AI responses is sent to third-party
                    text-to-speech providers to generate audio; no personal data
                    is included.
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h3 className="text-white font-semibold mb-2">
                    Payment Processors
                  </h3>
                  <p className="text-white/70 text-sm">
                    Web deposits are processed by licensed payment
                    intermediaries in Vietnam (e.g., SeePay, PayOS). Mobile
                    purchases and subscriptions are handled by the Apple App
                    Store, Google Play, and RevenueCat. We do not store your card
                    or bank details.
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h3 className="text-white font-semibold mb-2">
                    Media Storage &amp; Content Delivery
                  </h3>
                  <p className="text-white/70 text-sm">
                    Images, videos, and audio you upload are stored on and
                    delivered through secure third-party content delivery
                    networks (CDNs).
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h3 className="text-white font-semibold mb-2">
                    Error Monitoring
                  </h3>
                  <p className="text-white/70 text-sm">
                    Third-party error monitoring services may receive error
                    traces and technical context to diagnose issues. This is used
                    exclusively for debugging, not for advertising or profiling.
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-[#FF2E63]/10 border border-[#FF2E63]/30">
                  <h3 className="text-white font-semibold mb-2">
                    Advertising &amp; IP Address Processing
                  </h3>
                  <p className="text-white/70 text-sm mb-3">
                    The App may display advertising, including rewarded ads,
                    through Google's advertising platform (Google AdMob / Google
                    Mobile Ads). The advertising partner may process technical
                    signals, including your <strong>IP address</strong> and
                    advertising identifiers, to deliver, measure, and personalize
                    ads and to prevent fraud.
                  </p>
                  <p className="text-white/70 text-sm">
                    In the <strong>European Economic Area (EEA), the United
                    Kingdom, and Switzerland</strong>, Google may use the IP
                    address received from ad integrations for ad measurement and
                    personalization. Where required by law, we will display a
                    notice and obtain valid consent before processing for
                    advertising purposes, and provide a link explaining how
                    Google handles user data. You can adjust personalized
                    advertising and tracking choices in your device settings or
                    in the App where supported. On iOS, tracking for advertising
                    is enabled only if you allow it.
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h3 className="text-white font-semibold mb-2">
                    Cross-Border Transfers &amp; Business Transfers
                  </h3>
                  <p className="text-white/70 text-sm">
                    PHUONG is operated from Vietnam. By using the service, you
                    acknowledge your data may be transferred to and processed
                    outside Vietnam, including the United States and the European
                    Union, where the providers above operate, only with providers
                    maintaining adequate data protection. In a merger,
                    acquisition, or sale of assets, your data may be transferred
                    to the successor entity; we will notify you before it becomes
                    subject to a different privacy policy.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                5. Cookies &amp; Tracking Technologies
              </h2>
              <p className="mb-4">
                When you access our website through a browser and log in, we use
                an <strong>authentication cookie</strong> to keep you logged in
                and authenticate your requests to our servers. This cookie is
                strictly necessary for the platform to function. Any
                advertising-related technologies are described in Section 4.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                6. Data Retention &amp; Deletion
              </h2>
              <p className="mb-4">
                We retain the categories of data described in Section 2 for as
                long as your account is active in order to provide the service,
                and longer where necessary to comply with legal obligations,
                financial audits, fraud prevention, and security. Content you
                delete is removed from public view. Transaction history and
                certain logs may be retained even after account closure for the
                period required by applicable law. OTP codes expire after a short
                period.
              </p>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <h3 className="text-white font-semibold mb-2">
                  Account &amp; Data Deletion Requests
                </h3>
                <p className="text-white/70 text-sm">
                  You can request deletion of your account and data directly{" "}
                  <strong>in the App</strong> (the data deletion request
                  feature), or by email at{" "}
                  <a
                    href="mailto:contact@phuongai.com"
                    className="text-[#FF2E63] hover:underline"
                  >
                    contact@phuongai.com
                  </a>
                  . After we process your request, your access is revoked and
                  your personal data is removed from our active systems within a
                  reasonable period. Certain transaction data, security logs, or
                  technical backups may be retained to the extent necessary to
                  meet legal obligations and to prevent fraud and abuse.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Security</h2>
              <p className="mb-4">
                We apply security measures appropriate to industry standards to
                protect your data, including encryption of data in transit and
                not storing sensitive payment information. However, no method of
                transmission over the Internet is 100% secure; we cannot
                guarantee absolute security but are committed to protecting your
                data using sound practices.
              </p>
              <p>
                In the event of a data breach that poses a risk to your rights
                and freedoms, we will notify affected users and the relevant
                authorities as soon as practicable, in accordance with applicable
                law.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                8. Your Rights &amp; Choices
              </h2>
              <p className="mb-4">
                Depending on your location (including EU/EEA residents under GDPR
                and California residents under CCPA/CPRA where applicable), you
                may have the following rights:
              </p>
              <ul className="list-disc list-inside space-y-3 ml-4 mb-4">
                <li>
                  <strong>Access, Correction, Erasure &amp; Portability:</strong>{" "}
                  Request a copy, correction, or deletion of your data, or your
                  data in a machine-readable format.
                </li>
                <li>
                  <strong>Object / Withdraw Consent:</strong> Object to or
                  withdraw consent for AI personalization at any time. Withdrawal
                  does not affect the lawfulness of prior processing.
                </li>
                <li>
                  <strong>Reset AI Memory:</strong> Request a reset of your AI
                  companion's memory without deleting your account, clearing
                  PHUONG's personalized understanding while keeping your account
                  and data.
                </li>
                <li>
                  <strong>Automated Decisions:</strong> PHUONG's AI responses are
                  informational and advisory and do not constitute legally
                  binding automated decisions.
                </li>
                <li>
                  <strong>Where applicable:</strong> the right to opt out of the
                  "sale" or "sharing" of data for advertising purposes.
                </li>
              </ul>
              <p>
                To exercise any of these rights, contact us at{" "}
                <a
                  href="mailto:contact@phuongai.com"
                  className="text-[#FF2E63] hover:underline"
                >
                  contact@phuongai.com
                </a>
                .
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                9. Minors' Privacy
              </h2>
              <p>
                The service is intended for users who meet the minimum required
                age and is not directed at children. We do not knowingly collect
                personal data from anyone below the required age. If we become
                aware that an account belongs to an underage user, we will
                suspend the account and delete the associated data.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                10. Changes &amp; Acceptance of the Latest Version
              </h2>
              <p>
                We may update this Privacy Policy from time to time. When we make
                material changes, we will notify you via in-app notification or
                email.{" "}
                <strong>
                  Your continued use of PHUONG after the effective date
                  constitutes acceptance of the latest version of this policy.
                </strong>{" "}
                To continue using the service, you must agree to the current
                version; if you do not agree, please stop using the service.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                11. Contact Us
              </h2>
              <p className="mb-4">
                If you have questions, concerns, or requests regarding this
                Privacy Policy, please contact us at:
              </p>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-3">
                <div>
                  <p className="text-white/60 mb-1 text-sm">
                    Developer / Data Controller:
                  </p>
                  <p className="text-white font-medium">Phan Phúc Đạt</p>
                </div>
                <div>
                  <p className="text-white/60 mb-1 text-sm">Location:</p>
                  <p className="text-white font-medium">Vietnam</p>
                </div>
                <div>
                  <p className="text-white/60 mb-1 text-sm">Email:</p>
                  <a
                    href="mailto:contact@phuongai.com"
                    className="text-[#FF2E63] hover:underline text-lg font-medium"
                  >
                    contact@phuongai.com
                  </a>
                </div>
              </div>
            </section>
          </motion.div>
        </div>
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
};

export default PrivacyPolicyPage;
